/**
 * UrbanNetworkContext - Estado global de red urbana canónica
 *
 * @version 1.0
 * @created 2026-02-28
 * @module core/spatial
 *
 * @description
 * Gestiona el estado de la red vial canónica del proyecto:
 * - Estado activo (Power ON por defecto si hay red)
 * - Visibilidad en mapa (Eye ON por defecto)
 * - Interactividad (clicks en edges - OFF por defecto)
 * - Estadísticas (nodos, edges, longitud)
 *
 * Arquitectura similar a GridsContext:
 * - Power: habilita la opción de interactividad (ON por defecto)
 * - Eye: muestra/oculta edges en mapa (ON por defecto)
 * - Botón MapControls: activa/desactiva clicks en edges (independiente de Power)
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { NetworkStats } from './types';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

/**
 * Estado del contexto de red urbana
 */
export interface UrbanNetworkContextState {
  /** Si la red está activa (Power ON) - habilita interactividad */
  isNetworkActive: boolean;
  /** Si la red es visible en el mapa (Eye ON) */
  isNetworkVisible: boolean;
  /** Si la interactividad está activa (clicks en edges) */
  isNetworkInteractive: boolean;
  /** Estadísticas de la red (null si no está cargada) */
  networkStats: NetworkStats | null;
  /** Si está cargando estadísticas */
  loadingStats: boolean;
  /** Si está cargando la red urbana (edges + nodes) - FASE 1A */
  loadingNetwork: boolean;
  /** SPIKE: Si el mapa está listo y el fetch del backbone ya se disparó (spinner oculto inmediatamente) */
  previewNetworkBooting: boolean;
  /** SPIKE: Si el preview backbone ya está visible en el mapa */
  previewNetworkActive: boolean;
  /** Edge seleccionado (ID) */
  selectedEdgeId: string | null;
  /** Edge con hover (ID) */
  hoveredEdgeId: string | null;
  /** Nodo con hover (ID) */
  hoveredNodeId: string | null;
}

/**
 * Acciones del contexto de red urbana
 */
export interface UrbanNetworkContextActions {
  /** Activar/desactivar red (Power toggle) */
  setIsNetworkActive: (active: boolean) => void;
  /** Mostrar/ocultar red en mapa (Eye toggle) */
  setIsNetworkVisible: (visible: boolean) => void;
  /** Activar/desactivar interactividad (MapControls button) */
  setIsNetworkInteractive: (interactive: boolean) => void;
  /** Actualizar estadísticas de red */
  setNetworkStats: (stats: NetworkStats | null) => void;
  /** Cambiar estado de carga de stats */
  setLoadingStats: (loading: boolean) => void;
  /** Cambiar estado de carga de red - FASE 1A */
  setLoadingNetwork: (loading: boolean) => void;
  /** SPIKE: Señalizar que el mapa está listo y el fetch ya se disparó */
  setPreviewNetworkBooting: (booting: boolean) => void;
  /** SPIKE: Señalizar que el preview backbone es visible */
  setPreviewNetworkActive: (active: boolean) => void;
  /** Seleccionar edge */
  setSelectedEdgeId: (edgeId: string | null) => void;
  /** Hover en edge */
  setHoveredEdgeId: (edgeId: string | null) => void;
  /** Hover en nodo */
  setHoveredNodeId: (nodeId: string | null) => void;
  /** Reset completo del estado */
  reset: () => void;
}

/**
 * Tipo completo del contexto
 */
export type UrbanNetworkContextType = UrbanNetworkContextState & UrbanNetworkContextActions;

// ═══════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════

const UrbanNetworkContext = createContext<UrbanNetworkContextType | null>(null);

// ═══════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════

interface UrbanNetworkProviderProps {
  children: ReactNode;
}

const INITIAL_STATE: UrbanNetworkContextState = {
  isNetworkActive: true,  // ✨ CORRECCIÓN: Power ON por defecto - Red Urbana es el estado base del proyecto
  isNetworkVisible: true,  // Visible por defecto (edges/líneas visibles desde el inicio)
  isNetworkInteractive: false,  // Interactividad OFF (se activa desde MapControls)
  networkStats: null,
  loadingStats: false,
  loadingNetwork: false,  // FASE 1A: Inicializado en false, se controla en useCanonicalNetworkLayer
  previewNetworkBooting: false,
  previewNetworkActive: false,
  selectedEdgeId: null,
  hoveredEdgeId: null,
  hoveredNodeId: null,
};

export function UrbanNetworkProvider({ children }: UrbanNetworkProviderProps): JSX.Element {
  // ── State ──────────────────────────────────────────────────
  const [isNetworkActive, setIsNetworkActive] = useState(INITIAL_STATE.isNetworkActive);
  const [isNetworkVisible, setIsNetworkVisible] = useState(INITIAL_STATE.isNetworkVisible);
  const [isNetworkInteractive, setIsNetworkInteractive] = useState(INITIAL_STATE.isNetworkInteractive);
  const [networkStats, setNetworkStats] = useState<NetworkStats | null>(INITIAL_STATE.networkStats);
  const [loadingStats, setLoadingStats] = useState(INITIAL_STATE.loadingStats);
  const [loadingNetwork, setLoadingNetwork] = useState(INITIAL_STATE.loadingNetwork); // FASE 1A
  const [previewNetworkBooting, setPreviewNetworkBooting] = useState(INITIAL_STATE.previewNetworkBooting);
  const [previewNetworkActive, setPreviewNetworkActive] = useState(INITIAL_STATE.previewNetworkActive);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(INITIAL_STATE.selectedEdgeId);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(INITIAL_STATE.hoveredEdgeId);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(INITIAL_STATE.hoveredNodeId);

  // ── Actions ────────────────────────────────────────────────
  const reset = useCallback(() => {
    setIsNetworkActive(INITIAL_STATE.isNetworkActive);
    setIsNetworkVisible(INITIAL_STATE.isNetworkVisible);
    setIsNetworkInteractive(INITIAL_STATE.isNetworkInteractive);
    setNetworkStats(INITIAL_STATE.networkStats);
    setLoadingStats(INITIAL_STATE.loadingStats);
    setLoadingNetwork(INITIAL_STATE.loadingNetwork); // FASE 1A
    setPreviewNetworkBooting(INITIAL_STATE.previewNetworkBooting);
    setPreviewNetworkActive(INITIAL_STATE.previewNetworkActive);
    setSelectedEdgeId(INITIAL_STATE.selectedEdgeId);
    setHoveredEdgeId(INITIAL_STATE.hoveredEdgeId);
    setHoveredNodeId(INITIAL_STATE.hoveredNodeId);
  }, []);

  // ── Context Value ──────────────────────────────────────────
  const value: UrbanNetworkContextType = {
    // State
    isNetworkActive,
    isNetworkVisible,
    isNetworkInteractive,
    networkStats,
    loadingStats,
    loadingNetwork, // FASE 1A
    previewNetworkBooting,
    previewNetworkActive,
    selectedEdgeId,
    hoveredEdgeId,
    hoveredNodeId,
    // Actions
    setIsNetworkActive,
    setIsNetworkVisible,
    setIsNetworkInteractive,
    setNetworkStats,
    setLoadingStats,
    setLoadingNetwork, // FASE 1A
    setPreviewNetworkBooting,
    setPreviewNetworkActive,
    setSelectedEdgeId,
    setHoveredEdgeId,
    setHoveredNodeId,
    reset,
  };

  return (
    <UrbanNetworkContext.Provider value={value}>
      {children}
    </UrbanNetworkContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════

/**
 * Hook para acceder al contexto de red urbana
 *
 * @throws Si se usa fuera de UrbanNetworkProvider
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { isNetworkActive, setIsNetworkActive } = useUrbanNetwork();
 *
 *   const handleTogglePower = () => {
 *     setIsNetworkActive(!isNetworkActive);
 *   };
 *
 *   return <button onClick={handleTogglePower}>Toggle Power</button>;
 * }
 * ```
 */
export function useUrbanNetwork(): UrbanNetworkContextType {
  const context = useContext(UrbanNetworkContext);
  if (!context) {
    throw new Error('useUrbanNetwork must be used within UrbanNetworkProvider');
  }
  return context;
}

/**
 * Hook seguro (no lanza error si está fuera del provider)
 *
 * @returns Contexto o null
 */
export function useUrbanNetworkSafe(): UrbanNetworkContextType | null {
  return useContext(UrbanNetworkContext);
}
